import { useState, useEffect, useCallback } from 'react';
import {
  fetchBoardList, fetchBoardOne, createBoard, updateBoard, deleteBoard,
  toggleReaction, createComment, updateComment, deleteComment, updateBoardStatus
} from '../api/adminBoardApi';
import Toast from '../components/Toast';



const PAGE_SIZE = 20;

const TYPE_MAP = {
  '1': { label: '추가', color: '#1677ff', bg: '#e6f4ff', border: '#91caff' },
  '2': { label: '수정', color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f' },
  '3': { label: '오류', color: '#ff4d4f', bg: '#fff2f0', border: '#ffccc7' },
  '4': { label: '기타', color: '#888',    bg: '#f5f5f5', border: '#d9d9d9' },
};

const STATUS_MAP = {
  '1': { label: '접수대기', color: '#888',    bg: '#f5f5f5', border: '#d9d9d9' },
  '2': { label: '접수완료', color: '#faad14', bg: '#fffbe6', border: '#ffe58f' },
  '3': { label: '처리중',   color: '#1677ff', bg: '#e6f4ff', border: '#91caff' },
  '4': { label: '처리완료', color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f' },
};

const Badge = ({ map, value }) => {
  const info = map[value] || { label: value, color: '#888', bg: '#f5f5f5', border: '#d9d9d9' };
  return (
    <span style={{ background: info.bg, color: info.color, border: `1px solid ${info.border}`, borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {info.label}
    </span>
  );
};

export default function AdminBoardPage() {
  const [view,         setView]         = useState('list');
  const [list,         setList]         = useState([]);
  const [total,        setTotal]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [selected,     setSelected]     = useState(null);
  const [form,         setForm]         = useState({ title: '', content: '', img_data: null, type: '1' });
  const [isEdit,       setIsEdit]       = useState(false);
  const [comment, setComment] = useState('');
  const [editingCommentNo, setEditingCommentNo] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [toast,        setToast]        = useState('');
  const [loading,      setLoading]      = useState(false);
  const [searchType,   setSearchType]   = useState('');
  const [searchStatus, setSearchStatus] = useState('');
  const [searchTitle,  setSearchTitle]  = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusValue, setStatusValue] = useState('1');

  const user       = JSON.parse(localStorage.getItem('user') || '{}');
  const myKey      = `${user.branch_code}_${user.division_code}_${user.emp_no}`;
  const isRoot     = user.user_level === '0';
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const loadList = useCallback(async (p = page, type = searchType, status = searchStatus, title = searchTitle) => {
    setLoading(true);
    try {
      const res = await fetchBoardList(p, PAGE_SIZE, type, status, title);
      if (res.success) { setList(res.data); setTotal(res.total); }
    } finally { setLoading(false); }
  }, [page, searchType, searchStatus, searchTitle]);

  useEffect(() => { loadList(); }, [page]);

  const handleSearch = () => { setPage(1); loadList(1, searchType, searchStatus, searchTitle); };
  const handleReset  = () => {
    setSearchType(''); setSearchStatus(''); setSearchTitle('');
    setPage(1); loadList(1, '', '', '');
  };

  const handleDetail = async (seq_no) => {
    const res = await fetchBoardOne(seq_no);
    if (res.success) { setSelected(res.data); setView('detail'); }
  };

  const handleCreate = () => {
    setForm({ title: '', content: '', img_data: null, type: '1' });
    setIsEdit(false);
    setView('form');
  };

  const handleEdit = () => {
    setForm({ title: selected.title, content: selected.content || '', img_data: selected.img_data || null, type: selected.type || '1', status: selected.status || '1' });
    setIsEdit(true);
    setView('form');
  };

  const handleSave = async () => {
    if (!form.title.trim()) { showToast('❌ 제목을 입력해주세요.'); return; }
    setLoading(true);
    try {
      const res = isEdit
        ? await updateBoard(selected.seq_no, form)
        : await createBoard(form);
      if (res.success) {
        showToast(isEdit ? '✅ 수정되었습니다.' : '✅ 등록되었습니다.');
        await loadList(1);
        setPage(1);
        setView('list');
      } else showToast(`❌ ${res.message}`);
    } finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('게시글을 삭제하시겠습니까?')) return;
    const res = await deleteBoard(selected.seq_no);
    if (res.success) {
      showToast('✅ 삭제되었습니다.');
      await loadList(1); setPage(1); setView('list');
    } else showToast(`❌ ${res.message}`);
  };

  const startEditComment = (c) => {
    setEditingCommentNo(c.comment_no);
    setEditContent(c.content);
  };

  const cancelEditComment = () => {
    setEditingCommentNo(null);
    setEditContent('');
  };

  const handleUpdateComment = async (comment_no) => {
    if (!editContent.trim()) return;
    const res = await updateComment(selected.seq_no, comment_no, editContent);
    if (res.success) {
      setEditingCommentNo(null);
      setEditContent('');
      const updated = await fetchBoardOne(selected.seq_no);
      if (updated.success) setSelected(updated.data);
    } else showToast(`❌ ${res.message}`);
  };

  const handleReaction = async (type) => {
    const res = await toggleReaction(selected.seq_no, type);
    if (res.success) {
      const updated = await fetchBoardOne(selected.seq_no);
      if (updated.success) setSelected(updated.data);
    }
  };

  const handleComment = async () => {
    if (!comment.trim()) return;
    const res = await createComment(selected.seq_no, comment);
    if (res.success) {
      setComment('');
      const updated = await fetchBoardOne(selected.seq_no);
      if (updated.success) setSelected(updated.data);
    } else showToast(`❌ ${res.message}`);
  };

  const handleDeleteComment = async (comment_no) => {
    if (!window.confirm('댓글을 삭제하시겠습니까?')) return;
    const res = await deleteComment(selected.seq_no, comment_no);
    if (res.success) {
      const updated = await fetchBoardOne(selected.seq_no);
      if (updated.success) setSelected(updated.data);
    }
  };

  const handleImgUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { showToast('❌ 이미지는 1MB 이하만 업로드 가능합니다.'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => set('img_data', ev.target.result);
    reader.readAsDataURL(file);
  };

  const openStatusModal = () => {
    setStatusValue(selected.status || '1');
    setShowStatusModal(true);
  };

  const handleUpdateStatus = async () => {
    const res = await updateBoardStatus(selected.seq_no, statusValue);
    if (res.success) {
      setShowStatusModal(false);
      const updated = await fetchBoardOne(selected.seq_no);
      if (updated.success) setSelected(updated.data);
      showToast('✅ 처리상태가 변경되었습니다.');
    } else showToast(`❌ ${res.message}`);
  };

  const isMyPost = selected && `${selected.branch_code}_${selected.division_code}_${selected.emp_no}` === myKey;

  // ==================== 폼 화면 ====================
  if (view === 'form') return (
    <div style={S.wrap}>
      <Toast msg={toast} />
      <div style={S.header}>
        <button style={S.backBtn} onClick={() => setView(isEdit ? 'detail' : 'list')}>← 뒤로</button>
        <h2 style={S.title}>{isEdit ? '게시글 수정' : '게시글 등록'}</h2>
      </div>
      <div style={S.card}>
        {/* 구분 */}
        <div style={S.field}>
          <label style={S.label}>구분 *</label>
          <select style={S.input} value={form.type} onChange={e => set('type', e.target.value)}>
            {Object.entries(TYPE_MAP).map(([val, info]) => (
              <option key={val} value={val}>{info.label}</option>
            ))}
          </select>
        </div>

        {/* 처리상태 — 루트만 수정 가능 */}
        {isEdit && isRoot && (
          <div style={S.field}>
            <label style={S.label}>처리상태</label>
            <select style={S.input} value={form.status} onChange={e => set('status', e.target.value)}>
              {Object.entries(STATUS_MAP).map(([val, info]) => (
                <option key={val} value={val}>{info.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* 제목 */}
        <div style={S.field}>
          <label style={S.label}>제목 *</label>
          <input style={S.input} value={form.title}
            onChange={e => set('title', e.target.value)} placeholder="제목 입력" />
        </div>

        {/* 내용 */}
        <div style={S.field}>
          <label style={S.label}>내용</label>
          <textarea style={{ ...S.input, minHeight: 200, resize: 'vertical' }}
            value={form.content}
            onChange={e => set('content', e.target.value)} placeholder="내용 입력" />
        </div>

        {/* 이미지 */}
        <div style={S.field}>
          <label style={S.label}>이미지 (선택, 1MB 이하)</label>
          <input type="file" accept="image/*" onChange={handleImgUpload} />
          {form.img_data && (
            <div style={{ marginTop: 8 }}>
              <img src={form.img_data} alt="preview"
                style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, border: '1px solid #eee' }} />
              <button style={{ ...S.dangerBtn, marginTop: 4, padding: '2px 10px', fontSize: 12 }}
                onClick={() => set('img_data', null)}>이미지 삭제</button>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
          <button style={S.cancelBtn} onClick={() => setView(isEdit ? 'detail' : 'list')}>취소</button>
          <button style={{ ...S.primaryBtn, opacity: loading ? 0.7 : 1 }}
            onClick={handleSave} disabled={loading}>
            {loading ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );

  // ==================== 상세 화면 ====================
  if (view === 'detail' && selected) return (
    <div style={S.wrap}>
      <Toast msg={toast} />
      <div style={S.header}>
        <button style={S.backBtn} onClick={() => { setView('list'); loadList(page); }}>← 목록</button>
        <h2 style={S.title}>관리자 게시판</h2>
      </div>

      <div style={S.card}>
        {/* 배지 */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <Badge map={TYPE_MAP}   value={selected.type} />
          <Badge map={STATUS_MAP} value={selected.status} />
        </div>

        {/* 제목/작성자 */}
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 18, fontWeight: 800, color: '#1a1a2e', margin: '0 0 8px' }}>{selected.title}</h3>
          <div style={{ display: 'flex', gap: 16, fontSize: 13, color: '#888', flexWrap: 'wrap' }}>
            <span>✍️ {selected.division_name} {selected.name}</span>
            <span>📅 {selected.reg_dt}</span>
            <span>👁 {selected.views}</span>
          </div>
        </div>

        {/* 이미지 */}
        {selected.img_data && (
          <img src={selected.img_data} alt="첨부이미지"
            style={{ width: '100%', borderRadius: 8, marginBottom: 16, objectFit: 'contain', maxHeight: 400, background: '#f5f5f5' }} />
        )}

        {/* 내용 */}
        <div style={{ fontSize: 15, color: '#333', lineHeight: 1.8, whiteSpace: 'pre-wrap', minHeight: 100, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
          {selected.content || ''}
        </div>

        {/* 반응 + 버튼 */}
        <div style={{ display: 'flex', gap: 12, marginTop: 20, paddingTop: 16, borderTop: '1px solid #f0f0f0', flexWrap: 'wrap' }}>
          <button
            style={{ ...S.reactionBtn, background: selected.my_reaction === 'L' ? '#e6f4ff' : '#f5f5f5', color: selected.my_reaction === 'L' ? '#1677ff' : '#666', border: `1px solid ${selected.my_reaction === 'L' ? '#91caff' : '#e0e0e0'}` }}
            onClick={() => handleReaction('L')}>
            👍 {selected.like_cnt}
          </button>
          <button
            style={{ ...S.reactionBtn, background: selected.my_reaction === 'D' ? '#fff2f0' : '#f5f5f5', color: selected.my_reaction === 'D' ? '#ff4d4f' : '#666', border: `1px solid ${selected.my_reaction === 'D' ? '#ffccc7' : '#e0e0e0'}` }}
            onClick={() => handleReaction('D')}>
            👎 {selected.dislike_cnt}
          </button>
          {(isMyPost || isRoot) && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              {isMyPost && <button style={S.editBtn} onClick={handleEdit}>수정</button>}
              {isMyPost && <button style={S.dangerBtn} onClick={handleDelete}>삭제</button>}
              {!isMyPost && isRoot && <button style={S.editBtn} onClick={openStatusModal}>처리상태 변경</button>}
            </div>
          )}
        </div>
      </div>

      {/* 댓글 */}
      <div style={{ ...S.card, marginTop: 12 }}>
        <h4 style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', margin: '0 0 14px' }}>
          💬 댓글 {selected.comments?.length || 0}
        </h4>
        {selected.comments?.length === 0 && (
          <div style={{ textAlign: 'center', color: '#aaa', padding: '20px 0', fontSize: 13 }}>첫 번째 댓글을 남겨보세요!</div>
        )}
        {selected.comments?.map(c => {
          const isMine = `${c.branch_code}_${c.division_code}_${c.emp_no}` === myKey;
          const isEditing = editingCommentNo === c.comment_no;
          return (
            <div key={c.comment_no} style={{ padding: '12px 0', borderBottom: '1px solid #f5f5f5' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}>{c.division_name} {c.name}</span>
                  <span style={{ fontSize: 12, color: '#aaa', marginLeft: 8 }}>{c.reg_dt}</span>
                </div>
                {isMine && !isEditing && (
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button style={{ background: 'none', border: 'none', fontSize: 12, color: '#1677ff', cursor: 'pointer' }}
                      onClick={() => startEditComment(c)}>수정</button>
                    <button style={{ background: 'none', border: 'none', fontSize: 12, color: '#ff4d4f', cursor: 'pointer' }}
                      onClick={() => handleDeleteComment(c.comment_no)}>삭제</button>
                  </div>
                )}
              </div>

              {isEditing ? (
                <div style={{ marginTop: 8 }}>
                  <textarea
                    style={{ ...S.input, width: '100%', minHeight: 40, resize: 'vertical', boxSizing: 'border-box' }}
                    value={editContent}
                    onChange={e => setEditContent(e.target.value)}
                    autoFocus
                  />
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 6 }}>
                    <button style={S.cancelBtn} onClick={cancelEditComment}>취소</button>
                    <button style={S.primaryBtn} onClick={() => handleUpdateComment(c.comment_no)}>저장</button>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 14, color: '#333', marginTop: 6, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{c.content}</div>
              )}
            </div>
          );
        })}
        <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'flex-end' }}>
          <textarea
            style={{ ...S.input, flex: 1, minHeight: 40, maxHeight: 160, resize: 'vertical', fontFamily: "'Noto Sans KR', sans-serif" }}
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="댓글을 입력하세요"
          />
          <button style={S.primaryBtn} onClick={handleComment}>등록</button>
        </div>
      </div>

      {/* 처리상태 변경 모달 — 댓글 유무와 무관하게 항상 렌더링 가능하도록 최상위로 이동 */}
      {showStatusModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 320 }}>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4, color: '#1a1a2e' }}>처리상태 변경</div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>{selected.title}</div>
            <select style={{ ...S.input, width: '100%' }} value={statusValue} onChange={e => setStatusValue(e.target.value)}>
              {Object.entries(STATUS_MAP).map(([val, info]) => (
                <option key={val} value={val}>{info.label}</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button style={S.cancelBtn} onClick={() => setShowStatusModal(false)}>취소</button>
              <button style={S.primaryBtn} onClick={handleUpdateStatus}>저장</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ==================== 목록 화면 ====================
  return (
    <div style={S.wrap}>
      <Toast msg={toast} />
      <div style={S.header}>
        <h2 style={S.title}>📋 관리자 게시판</h2>
        <button style={S.primaryBtn} onClick={handleCreate}>+ 글쓰기</button>
      </div>

      {/* 검색 */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '12px 14px', marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <select style={S.searchInput} value={searchType} onChange={e => setSearchType(e.target.value)}>
          <option value="">전체 구분</option>
          {Object.entries(TYPE_MAP).map(([val, info]) => (
            <option key={val} value={val}>{info.label}</option>
          ))}
        </select>
        <select style={S.searchInput} value={searchStatus} onChange={e => setSearchStatus(e.target.value)}>
          <option value="">전체 처리상태</option>
          {Object.entries(STATUS_MAP).map(([val, info]) => (
            <option key={val} value={val}>{info.label}</option>
          ))}
        </select>
        <input style={{ ...S.searchInput, flex: 1, minWidth: 120 }}
          placeholder="🔍 제목 검색"
          value={searchTitle}
          onChange={e => setSearchTitle(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()} />
        <button style={S.searchBtn} onClick={handleSearch}>검색</button>
        <button style={{ ...S.searchBtn, background: '#888' }} onClick={handleReset}>초기화</button>
      </div>

      {/* 리스트 */}
      <div style={S.card}>
        {loading ? (
          <div style={S.empty}>⏳ 불러오는 중...</div>
        ) : list.length === 0 ? (
          <div style={S.empty}>등록된 게시글이 없습니다.</div>
        ) : list.map(item => (
          <div key={item.seq_no} style={S.listItem}
            onClick={() => handleDetail(item.seq_no)}
            onMouseEnter={e => e.currentTarget.style.background = '#f8faff'}
            onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                <Badge map={TYPE_MAP}   value={item.type} />
                <Badge map={STATUS_MAP} value={item.status} />
                <span style={{ fontSize: 15, fontWeight: 600, color: '#1a1a2e',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.title}
                  {item.comment_cnt > 0 && (
                    <span style={{ fontSize: 12, color: '#1677ff', marginLeft: 6 }}>[{item.comment_cnt}]</span>
                  )}
                </span>
              </div>
              <div style={{ fontSize: 12, color: '#aaa', display: 'flex', gap: 12 }}>
                <span>✍️ {item.division_name} {item.name}</span>
                <span>📅 {item.reg_dt?.slice(0, 10)}</span>
                <span>👁 {item.views}</span>
                {item.like_cnt > 0    && <span>👍 {item.like_cnt}</span>}
                {item.dislike_cnt > 0 && <span>👎 {item.dislike_cnt}</span>}
              </div>
            </div>
            <span style={{ fontSize: 20, color: '#ccc' }}>›</span>
          </div>
        ))}

        {totalPages > 1 && (
          <div style={S.pagination}>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button key={p}
                style={{ ...S.pageBtn, ...(p === page ? S.pageBtnActive : {}) }}
                onClick={() => setPage(p)}>{p}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  wrap        : { background: '#f5f7fa', padding: 16, fontFamily: "'Noto Sans KR', sans-serif" },
  header      : { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  title       : { fontSize: 20, fontWeight: 800, margin: 0, color: '#1a1a2e' },
  card        : { background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  field       : { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 },
  label       : { fontSize: 13, fontWeight: 600, color: '#444' },
  input       : { border: '1.5px solid #e0e0e0', borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', background: '#fafafa', width: '100%', boxSizing: 'border-box', fontFamily: "'Noto Sans KR', sans-serif" },
  searchInput : { border: '1.5px solid #e0e0e0', borderRadius: 8, padding: '8px 12px', fontSize: 14, outline: 'none', background: '#fafafa' },
  searchBtn   : { background: '#1677ff', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  listItem    : { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 4px', borderBottom: '1px solid #f5f5f5', cursor: 'pointer', transition: 'background 0.15s' },
  empty       : { textAlign: 'center', padding: '60px 0', color: '#bbb', fontSize: 14 },
  pagination  : { display: 'flex', justifyContent: 'center', gap: 6, padding: '14px 0' },
  pageBtn     : { width: 30, height: 30, borderRadius: 6, border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#666' },
  pageBtnActive : { background: '#1677ff', color: '#fff', border: '1px solid #1677ff' },
  primaryBtn  : { background: '#1677ff', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  cancelBtn   : { background: '#fff', border: '1px solid #e0e0e0', color: '#666', borderRadius: 8, padding: '10px 18px', fontSize: 14, cursor: 'pointer' },
  editBtn     : { background: '#fff', border: '1.5px solid #1677ff', color: '#1677ff', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  dangerBtn   : { background: '#fff2f0', border: '1px solid #ffccc7', color: '#ff4d4f', borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer' },
  reactionBtn : { border: 'none', borderRadius: 20, padding: '8px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  backBtn     : { background: 'none', border: 'none', fontSize: 14, color: '#1677ff', cursor: 'pointer', padding: 0 },
};